import React, { useState, useEffect } from 'react';
import { X, Search, Users, CheckSquare, Square } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

import { BACKEND_URL } from '../../config/api';
const AssignStudentsDialog = ({ mentor, isOpen, onClose, onAssignmentComplete }) => {
  const [students, setStudents] = useState([]);
  const [selectedStudents, setSelectedStudents] = useState(new Set());
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [assigning, setAssigning] = useState(false);
  const [currentAssignments, setCurrentAssignments] = useState(new Set());

  useEffect(() => {
    if (isOpen && mentor) {
      fetchStudents();
    }
  }, [isOpen, mentor]);

  const fetchStudents = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('lms_token');
      
      // Fetch all students
      const response = await axios.get(
        `${BACKEND_URL}/api/users`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      const allStudents = response.data.filter(u => u.role === 'student');
      setStudents(allStudents);
      
      // Get students already assigned to this mentor
      const assigned = new Set(
        allStudents
          .filter(s => s.mentor === mentor.email.toLowerCase())
          .map(s => s.id)
      );
      setCurrentAssignments(assigned);
      setSelectedStudents(new Set(assigned)); // Pre-select already assigned students
      
    } catch (error) {
      console.error('Error fetching students:', error);
      toast.error('Failed to load students');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectAll = () => {
    if (selectedStudents.size === filteredStudents.length) {
      setSelectedStudents(new Set());
    } else {
      setSelectedStudents(new Set(filteredStudents.map(s => s.id)));
    }
  };

  const handleToggleStudent = (studentId) => {
    const newSelected = new Set(selectedStudents);
    if (newSelected.has(studentId)) {
      newSelected.delete(studentId);
    } else {
      newSelected.add(studentId);
    }
    setSelectedStudents(newSelected);
  };

  const handleSaveAndAssign = async () => {
    try {
      setAssigning(true);
      const token = localStorage.getItem('lms_token');
      
      // Get students to assign (selected) and unassign (previously assigned but now deselected)
      const studentsToAssign = Array.from(selectedStudents);
      const studentsToUnassign = Array.from(currentAssignments).filter(
        id => !selectedStudents.has(id)
      );
      
      console.log('Assigning:', studentsToAssign.length, 'students');
      console.log('Unassigning:', studentsToUnassign.length, 'students');
      
      // Assign selected students (in request body to avoid URL length limits)
      if (studentsToAssign.length > 0) {
        const assignResponse = await axios.post(
          `${BACKEND_URL}/api/admin/mentor-assignments/assign-bulk?mentor_email=${encodeURIComponent(mentor.email)}&assign_all=false`,
          studentsToAssign,  // Send as array in body
          {
            headers: { 
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          }
        );
        console.log('Assign response:', assignResponse.data);
      }
      
      // Unassign deselected students
      if (studentsToUnassign.length > 0) {
        console.log('Unassigning student IDs:', studentsToUnassign);
        try {
          const unassignResponse = await axios.post(
            `${BACKEND_URL}/api/admin/mentor-assignments/unassign-bulk`,
            studentsToUnassign,  // Send as array directly
            { 
              headers: { 
                Authorization: `Bearer ${token}`,
                'Content-Type': 'application/json'
              } 
            }
          );
          console.log('Unassign response:', unassignResponse.data);
        } catch (unassignError) {
          console.error('Unassign error details:', unassignError.response?.data);
          // Don't fail the whole operation if unassign fails
          toast.error(`Warning: Could not unassign some students`);
        }
      }
      
      toast.success(
        `✅ Successfully assigned ${studentsToAssign.length} students to ${mentor.first_name || mentor.email}!`
      );
      
      onAssignmentComplete();
      onClose();
      
    } catch (error) {
      console.error('Error assigning students:', error);
      console.error('Error response:', error.response?.data);
      const errorMessage = error.response?.data?.detail || error.message || 'Unknown error';
      toast.error(`Failed to assign students: ${errorMessage}`);
    } finally {
      setAssigning(false);
    }
  };

  const filteredStudents = students.filter(student => {
    const query = searchQuery.toLowerCase();
    return (
      student.email?.toLowerCase().includes(query) ||
      student.first_name?.toLowerCase().includes(query) ||
      student.last_name?.toLowerCase().includes(query) ||
      `${student.first_name} ${student.last_name}`.toLowerCase().includes(query)
    );
  });

  const allSelected = filteredStudents.length > 0 && selectedStudents.size === filteredStudents.length;
  const someSelected = selectedStudents.size > 0 && selectedStudents.size < filteredStudents.length;

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="p-6 border-b border-gray-200 flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <Users className="w-6 h-6 text-blue-600" />
              Assign Students to {mentor?.first_name || mentor?.email}
            </h2>
            <p className="text-sm text-gray-600 mt-1">
              Select students to assign to this mentor
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Search and Select All */}
        <div className="p-6 border-b border-gray-200 space-y-4">
          <div className="relative">
            <Search className="absolute left-3 top-3 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search students by name or email..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          <div className="flex items-center justify-between">
            <button
              onClick={handleSelectAll}
              className="flex items-center gap-2 px-4 py-2 bg-blue-50 text-blue-700 rounded-lg hover:bg-blue-100 transition-colors font-medium"
            >
              {allSelected ? (
                <>
                  <CheckSquare className="w-5 h-5" />
                  Deselect All
                </>
              ) : (
                <>
                  <Square className="w-5 h-5" />
                  Select All
                </>
              )}
            </button>

            <div className="text-sm text-gray-600">
              <span className="font-semibold text-blue-600">{selectedStudents.size}</span> of{' '}
              <span className="font-semibold">{filteredStudents.length}</span> students selected
            </div>
          </div>
        </div>

        {/* Student List */}
        <div className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            </div>
          ) : filteredStudents.length === 0 ? (
            <div className="text-center py-12">
              <Users className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-600">No students found</p>
            </div>
          ) : (
            <div className="space-y-2">
              {filteredStudents.map((student) => {
                const isSelected = selectedStudents.has(student.id);
                const wasAssigned = currentAssignments.has(student.id);
                
                return (
                  <div
                    key={student.id}
                    onClick={() => handleToggleStudent(student.id)}
                    className={`p-4 border rounded-lg cursor-pointer transition-all ${
                      isSelected
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    <div className="flex items-center gap-4">
                      {/* Checkbox */}
                      <div className={`w-5 h-5 rounded border-2 flex items-center justify-center ${
                        isSelected
                          ? 'bg-blue-600 border-blue-600'
                          : 'border-gray-300'
                      }`}>
                        {isSelected && (
                          <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                          </svg>
                        )}
                      </div>

                      {/* Avatar */}
                      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white font-bold">
                        {student.first_name?.charAt(0) || student.email.charAt(0).toUpperCase()}
                      </div>

                      {/* Student Info */}
                      <div className="flex-1">
                        <h4 className="font-semibold text-gray-900">
                          {student.first_name && student.last_name
                            ? `${student.first_name} ${student.last_name}`
                            : student.email}
                        </h4>
                        <p className="text-sm text-gray-600">{student.email}</p>
                        {student.school_name && (
                          <p className="text-xs text-gray-500 mt-1">
                            {student.school_name} {student.grade && `• Grade ${student.grade}`}
                          </p>
                        )}
                      </div>

                      {/* Status Badge */}
                      {wasAssigned && (
                        <span className="px-2 py-1 bg-green-100 text-green-800 text-xs rounded-full font-medium">
                          Currently Assigned
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-gray-200 flex items-center justify-between bg-gray-50">
          <div className="text-sm text-gray-600">
            {selectedStudents.size > 0 && (
              <p>
                <span className="font-semibold text-blue-600">{selectedStudents.size}</span> students will be assigned
              </p>
            )}
          </div>

          <div className="flex gap-3">
            <button
              onClick={onClose}
              disabled={assigning}
              className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              onClick={handleSaveAndAssign}
              disabled={assigning || selectedStudents.size === 0}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed font-semibold flex items-center gap-2"
            >
              {assigning ? (
                <>
                  <span className="animate-spin">⏳</span>
                  Assigning...
                </>
              ) : (
                <>
                  <Users className="w-5 h-5" />
                  Save & Assign ({selectedStudents.size})
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AssignStudentsDialog;
